import { useCallback, useMemo } from 'react';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { AdsHistory } from '../types';
import { format } from 'date-fns';
import { CellContainer, CellTypography, Image, ImageSkeleton } from '../components/AdsHistoryGrid.styles';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ArrowUpward, Cancel, CheckCircle } from '@mui/icons-material';
import { useListParams } from 'hooks';

const columnsAdditionalOptions = {
  disableColumnFilter: true,
  disableColumnMenu: true,
};

export const useAdsHistoryColumns = (): GridColDef[] => {
  const { desc, setDesc, setOrderBy } = useListParams();

  const handleSortingByCreatedAt = useCallback(() => {
    setDesc(!desc);
    setOrderBy('created');
  }, [desc, setDesc, setOrderBy]);

  return useMemo(
    () => [
      {
        field: 'creative',
        headerName: 'Creative',
        width: 400,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AdsHistory>) => (
          <CellContainer>
            <Box sx={{ position: 'relative', mr: 1 }}>
              <ImageSkeleton />
              <Image
                src={params.row.creative.preview}
                alt={params.row.creative.name}
                loading="lazy"
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.opacity = '1';
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                }}
              />
            </Box>
            <CellTypography variant={'body2'}>
              {(params.row).creative.name}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'ad_account',
        headerName: 'Ad account',
        width: 220,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: (params: GridRenderCellParams<AdsHistory>) => (
          <CellContainer
            sx={{
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <CellTypography variant={'body2'}>
              {params.row.ad_account.name}
            </CellTypography>
            <CellTypography
              variant={'caption'}
              sx={(t) => ({
                color: t.palette.text.secondary,
              })}
            >
              {params.row.ad_account.id}
            </CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'ad_set',
        headerName: 'Adset',
        width: 400,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: (params: GridRenderCellParams<AdsHistory>) => (
          <CellContainer
            sx={{
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <CellTypography variant={'body2'}>
              {params.row.ad_set.name}
            </CellTypography>
            <CellTypography
              variant={'caption'}
              sx={(t) => ({
                color: t.palette.text.secondary,
              })}
            >
              {params.row.ad_set.id}
            </CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 80,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: (params: GridRenderCellParams<AdsHistory>) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {params.row.status ? (
                <CheckCircle
                  sx={(t) => ({
                    color: t.palette.success.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              ) : (
                <Cancel
                  sx={(t) => ({
                    color: t.palette.error.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              )}
            </CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'message',
        headerName: 'Message',
        sortable: false,
        width: 600,
        ...columnsAdditionalOptions,
        renderCell: (params: GridRenderCellParams<AdsHistory>) => (
          <CellContainer>
            <Tooltip
              title={params.row.message}
              placement={'bottom'}
            >
              <CellTypography variant={'body2'}>
                {params.row.message}
              </CellTypography>
            </Tooltip>
          </CellContainer>
        ),
      },
      {
        field: 'created',
        headerName: 'Created',
        renderHeader: () => {
          return (
            <CellContainer sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant={'subtitle2'}>Created</Typography>
              <IconButton
                sx={{ p: 0.5, transform: `rotate(${desc ? 0 : 180}deg)` }}
                onClick={handleSortingByCreatedAt}
              >
                <ArrowUpward sx={{ fontSize: 16 }} />
              </IconButton>
            </CellContainer>
          );
        },
        width: 240,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: (params: GridRenderCellParams<AdsHistory>) => {
          const { email, created_at } = params.row;
          return (
            <CellContainer
              sx={{
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <CellTypography variant={'body2'}>{email}</CellTypography>
              <CellTypography
                variant={email ? 'caption' : 'body2'}
                sx={(t) => ({
                  color: t.palette.text.secondary,
                })}
              >
                {format(new Date(created_at), 'yyyy-MM-dd')}
              </CellTypography>
            </CellContainer>
          );
        },
      },
    ],
    [desc, handleSortingByCreatedAt],
  );
};
